import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, Check, X, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  pending: { label: "Pending", className: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { label: "Approved", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Rejected", className: "bg-red-50 text-red-700 border-red-200" },
};

export default function SupplyRequests() {
  const { user } = useAuth();
  const isReviewer = user?.role === "admin" || user?.role === "full";
  const [items, setItems] = useState([]);
  const [parts, setParts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ part_id: "", quantity: 1, notes: "" });
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const [r, p] = await Promise.all([
      api.get("/supply-requests"),
      api.get("/parts"),
    ]);
    setItems(r.data);
    setParts(p.data.filter((item) => item.type === "supply"));
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.part_id) return toast.error("Select a part");
    setSaving(true);
    try {
      await api.post("/supply-requests", {
        part_id: form.part_id,
        quantity: Number(form.quantity),
        notes: form.notes,
      });
      toast.success("Request submitted");
      setOpen(false);
      setForm({ part_id: "", quantity: 1, notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const review = async (r, status) => {
    try {
      await api.patch(`/supply-requests/${r.id}`, { status });
      toast.success(status === "approved" ? "Approved" : "Rejected");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (r) => {
    if (!window.confirm("Delete this request?")) return;
    try {
      await api.delete(`/supply-requests/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const filtered = items.filter((r) => filter === "all" || r.status === filter);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10 max-w-6xl">
      <PageHeader
        eyebrow="Operations"
        title="Supply Requests"
        description={isReviewer ? "Review and approve requests from your team." : "Submit a request to restock a part you need."}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-request-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">
                <Plus className="w-4 h-4 mr-2" /> New Request
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl tracking-tight">New supply request</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="request-form">
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Part</Label>
                  <Select value={form.part_id} onValueChange={(v) => setForm({ ...form, part_id: v })}>
                    <SelectTrigger className="rounded-sm border-zinc-300" data-testid="request-part-select">
                      <SelectValue placeholder="Select a supply..." />
                    </SelectTrigger>
                    <SelectContent>
                      {parts.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name} ({p.sku}) — {p.quantity} in stock
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {parts.length === 0 && (
                    <div className="text-xs text-zinc-500 mt-1">
                      No supplies in inventory yet. Ask an admin to add items with type "Supply" on the Inventory page.
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Quantity</Label>
                  <Input required type="number" min={1} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="request-qty-input" className="rounded-sm border-zinc-300 font-mono" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="request-notes-input" className="rounded-sm border-zinc-300" rows={3} />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                  <Button type="submit" disabled={saving} data-testid="request-submit-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">
                    {saving ? "Submitting..." : "Submit"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="mt-6 flex items-center gap-2">
        {["all", "pending", "approved", "rejected"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`filter-${s}`}
            className={`px-3 py-1 text-xs tracking-[0.15em] uppercase font-bold rounded-sm border ${
              filter === s ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="mt-4 bg-white border border-zinc-200 rounded-sm overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              {["Status", "Part", "Qty", "Requester", "Notes", "Submitted", ""].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-zinc-500">No requests.</td></tr>
            )}
            {filtered.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.pending;
              return (
                <tr key={r.id} className="hover:bg-zinc-50" data-testid={`request-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 text-[10px] tracking-[0.15em] uppercase font-bold rounded-sm border ${meta.className}`}>
                      {meta.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-zinc-950">
                    <div className="font-semibold">{r.part_name}</div>
                    <div className="text-xs text-zinc-500 font-mono">{r.part_sku}</div>
                  </td>
                  <td className="px-4 py-3 text-sm font-mono font-bold text-zinc-900">{r.quantity}</td>
                  <td className="px-4 py-3 text-sm text-zinc-800">
                    {r.requester_name}
                    <div className="text-xs text-zinc-500 font-mono">{r.requester_phone || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-zinc-700 max-w-xs">
                    {r.notes ? <span className="line-clamp-2" title={r.notes}>{r.notes}</span> : <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500 font-mono">
                    {new Date(r.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {isReviewer && r.status === "pending" && (
                      <>
                        <button onClick={() => review(r, "approved")} data-testid={`approve-${r.id}`} className="text-emerald-600 hover:text-emerald-700 mr-2" title="Approve">
                          <Check className="w-4 h-4" />
                        </button>
                        <button onClick={() => review(r, "rejected")} data-testid={`reject-${r.id}`} className="text-[#FF2400] hover:text-red-700 mr-2" title="Reject">
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    <button onClick={() => remove(r)} data-testid={`delete-${r.id}`} className="text-zinc-400 hover:text-[#FF2400]" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
